import * as React from "react";
import {Fragment, useCallback, useEffect, useMemo, useState} from "react";
import {
    Badge,
    classNames,
    CloseButton,
    Dialog,
    DialogFooter,
    DialogHeader, Keyboard, KeyboardLayout,
    PushButton, TextInput, TextKeyboardLayout,
    layout, PhoneKeyboardLayout, theme, typography,
    useCanAutoDeactivateListener,
    useOccupancies,
    usePlatform, useTypeRef,
    View,
    ViewContent, ViewFooter, ViewFooterItem,
    ViewHeader,
    ViewHeaderItem
} from "@variocube/cube-ui";
import {CustomField, CustomFieldType, DelayReason, Rental, RentalState, RentalTypes} from "./types";
import {BoxControl} from "./admin/BoxControl";
import {isCurrent, isDelayed, useItems, useRentals, useTenant} from "./hooks";
import {DateTime} from "luxon";
import {ContactView} from "./ContactView";
import {useLocalization} from "../i18n";
import {stylesheet} from "typestyle";
import {percent} from "csx";

export interface RentalViewProps {
    rental: Rental;
    rentalTypes: RentalTypes;
    onClose: () => void;
}

enum TicketType {
    Defective = 'Defective',
    Lost = 'Lost',
    Maintenance = 'Maintenance',
    Misc = 'Misc'
}

interface CustomFieldInput extends CustomField {
    value: string;
}

export function RentalBorrowerView({rental, rentalTypes, onClose}: RentalViewProps) {
    const platform = usePlatform();
    const {t, language} = useLocalization();
    const occupancies = useOccupancies();
    const items = useItems();
    const rentals = useRentals();
    const tenant = useTenant();
    const occupancy = occupancies.find(occupancy => occupancy.content.item == rental.itemUuid);
    const item = items.find(item => item.uuid == rental.itemUuid);

    const [contactView, setContactView] = useState<boolean>(false);
    const [returnDialog, setReturnDialog] = useState(false);
    const [itemConditionDialog, setItemConditionDialog] = useState(false);
    const [delayReason, setDelayReason] = useState<DelayReason>();

    const [customFieldDialog, setCustomFieldDialog] = useState(false);
    const [customFieldInputs, setCustomFieldInputs] = useState<CustomFieldInput[]>([]);

    useEffect(() => {
        if (item?.customFields) {
            setCustomFieldInputs(item.customFields.map(f => ({
                ...f,
                value: ''
            })));
        }
    }, [item?.customFields]);

    // allow auto deactivate only when no returnDialog is shown
    const canAutoDeactivate = useCallback(() => !returnDialog, [returnDialog]);
    useCanAutoDeactivateListener(canAutoDeactivate);

    const current = isCurrent(rental) // is current by storage
        && (occupancy?.content.rental != rental.itemUuid || !occupancy?.content.rentalEnded); // is not locally ended

    const delayed = isDelayed(rental)
        && (occupancy?.content.rental != rental.itemUuid || !occupancy?.content.rentalEnded); // is not locally ended
    console.log('delayed: ' + delayed);

    const previousRental = useMemo<Rental | undefined>(() => {
        if(item) {
            return rentals
                .find((r) => (r.state == RentalState.BorrowerPickedUp || r.state == RentalState.LenderReturned ) && r.itemUuid == item.uuid && r.uuid != rental.uuid)
        }
        return undefined;
    }, [item, rentals, rental])

    const handleBoxOpened = useCallback(async () => {
        if (occupancy) {
            if (occupancy.content.rental != rental.uuid) {
                console.log(`Updating occupancy content to rental ${rental.uuid}`);
                try {
                    await platform.updateOccupancy(occupancy.uuid, {
                        item: rental.itemUuid,
                        rental: rental.uuid
                    });
                } catch (error) {
                    console.error("Could not set rental on occupancy", error);
                }
            }
            else {
                console.log('Checking of any end rental action needs to be taken');
                if (!occupancy?.content.rentalEnded && rental.borrower.pickedUp) {
                    console.log(`Borrower picked up at ${rental.borrower.pickedUp} and rental has not yet ended`)
                    console.log(rental.serviceItem);
                    if(rental.borrower.rentalEndsAfterReturn && (!rental.serviceItem || rental.state !== RentalState.BorrowerPickedUp )) {
                        console.log('Flag rentalEndsAfterReturn is set, ask user if they want to end the rental');
                        setReturnDialog(true);
                        return;
                    }
                    console.log(rental.borrower);

                    if(rental.borrower.rentalEndsAutomaticallyAfterReturn != undefined && (!rental.serviceItem || rental.state !== RentalState.BorrowerPickedUp )) {
                        console.log(`Flag rentalEndsAutomaticallyAfterReturn is set to: ${rental.borrower.rentalEndsAutomaticallyAfterReturn} minutes, automatically ending rental`);
                        await handleEndRental(rental.borrower.rentalEndsAutomaticallyAfterReturn);
                        return;
                    }
                }
            }
        }
    }, [platform, occupancy, rental]);

    const handleReturnDialogClose = useCallback(() => {
        console.log(`User wants to continue rental: ${rental.uuid}`);
        setReturnDialog(false);
    }, [rental]);

    const handleItemConditionDialogClose = () => setItemConditionDialog(false);

    const onEndRentalClick = (delayReason?: DelayReason) => {
        console.log(`User wants to end rental: ${rental.uuid}`);
        if (delayReason) {
            console.log(`User wants to end rental: ${rental.uuid}, with delay reason: ${delayReason}`);
            setDelayReason(delayReason);
        }
        if (customFieldInputs.length > 0) {
            console.log(`User wants to end rental: ${rental.uuid}, asking for custom fields`);
            setCustomFieldDialog(true);
        }
        else if (occupancy) {
            console.log(`User wants to end rental: ${rental.uuid}, asking for item condition`);
            setItemConditionDialog(true);
        }
    }

    function onCustomFieldSubmit() {
        setCustomFieldDialog(false);
        if (occupancy) {
            console.log(`User wants to end rental: ${rental.uuid}, asking for item condition`);
            setItemConditionDialog(true);
        }
    }

    function handleCustomFieldDialogClose() {
        setCustomFieldDialog(false);
    }

    function handleCustomFieldChange(slug: string, value: string) {
        setCustomFieldInputs(prev => prev.map(f => {
            if (f.slug === slug) {
                f.value = value;
            }
            return f;
        }))
    }

    async function onItemConditionSubmit(type?: TicketType, needInspection?: boolean, message?: string) {
        await handleEndRental(undefined, type, needInspection, message);
    }

    const handleEndRental = useCallback(async (plusMinutes?: number, createTicket?: TicketType, needInspection?: boolean, message?: string) => {
        if (occupancy) {
            let rentalEnded = DateTime.local();
            if(plusMinutes) {
                rentalEnded = DateTime.local().plus({minutes: plusMinutes});
            }
            console.log(`Ending rental with timestamp of ${rentalEnded}`);
            try {
                await platform.updateOccupancy(occupancy.uuid, {
                    rentalEnded: rentalEnded.toISO(),
                    createTicket,
                    needInspection,
                    message,
                    delayReason,
                    customFieldInputs
                }, true);
            } catch (error) {
                console.error("Could not set rental on occupancy", error);
            }
        }
        setItemConditionDialog(false);
        setReturnDialog(false);
    }, [occupancy, platform, delayReason, customFieldInputs]);

    if(contactView) {
        return (
            <ContactView onClose={() => setContactView(false)} />
        );
    }

    return (
        <View>
            <ViewHeader className={theme.lessContrast}>
                <ViewHeaderItem fill className={layout.paddingMedium}>
                    <div
                        className={typography.title}>{rentalTypes == RentalTypes.Loan ? t("Loan") : t("Rental")}</div>
                </ViewHeaderItem>
                <CloseButton label={t("Close")} onClick={onClose}/>
            </ViewHeader>
            <ViewContent className={classNames(layout.horizontal, layout.paddingMedium, layout.alignCenter, layout.spaceEvenly)}>

                <div className={typography.bigger}>
                    <div className={layout.marginYLarge}>
                        <div>{rental.borrower.name}</div>
                        <div>{rental.borrower.email}</div>
                        <div>{rental.borrower.phone}</div>
                    </div>

                    <div className={layout.marginYLarge}>
                        <div className={typography.caption}>{t("Timeframe")}</div>
                        <div className={layout.marginYSmall}>
                            <div>
                                {DateTime.fromISO(rental.from).setLocale(language).toLocaleString(DateTime.DATETIME_SHORT)}
                            </div>
                            <div>{t("TimeframeTo")}</div>
                            <div>
                                {DateTime.fromISO(rental.until).setLocale(language).toLocaleString(DateTime.DATETIME_SHORT)}
                            </div>
                        </div>
                    </div>

                    <div className={layout.marginYLarge}>
                        <div className={typography.caption}>{t("Item")}</div>
                        <div className={layout.marginYSmall}>
                            <div>{item?.name}</div>
                            <div>{item?.description}</div>
                            <div>{item?.foreignId}</div>
                        </div>
                    </div>
                </div>

                <div className={classNames(layout.fill, layout.vertical, layout.center, layout.alignCenter)}>
                    {!current && !delayed && (
                        <div className={typography.bigger} style={{maxWidth: "20em", paddingBottom: "1em"}}>
                            <Badge error>{t("RentalOutOfTimeframe")}</Badge>
                        </div>
                    )}
                    {!current && delayed && (
                        <div className={typography.bigger} style={{maxWidth: "20em", paddingBottom: "1em"}}>
                            <Badge warning>{t("RentalDelayed")}</Badge>
                        </div>
                    )}
                    {!occupancy && (
                        <div className={typography.bigger} style={{maxWidth: "20em", paddingBottom: "1em"}}>
                            <Badge error>{t("ItemNotStoredError")}</Badge>
                        </div>
                    )}
                    {(occupancy && (current || delayed)) && (
                        <div className={classNames(layout.marginMedium)}>
                            <div className={typography.title} style={{textAlign: 'center'}}>{t("Box")}</div>
                            {occupancy && (
                                <BoxControl boxNumber={occupancy.boxNumber} onBoxOpened={handleBoxOpened} disabled={Boolean(previousRental)} />
                            )}
                        </div>
                    )}
                </div>
            </ViewContent>
            <Dialog open={returnDialog} onBackdropClick={!delayed ? handleReturnDialogClose : undefined}>
                <DialogHeader title={t("EndRental")}/>
                {!delayed &&
                    <React.Fragment>
                        <div className={layout.paddingMedium}>
                            {t("EndRentalMessage")}
                        </div>
                        <DialogFooter>
                            <div className={layout.paddingMedium}>
                                <PushButton success onClick={handleReturnDialogClose}>{t("ContinueRental")}</PushButton>
                            </div>
                            <div className={layout.paddingMedium}>
                                <PushButton danger onClick={() => onEndRentalClick()}>
                                    {t("EndRental")}
                                </PushButton>
                            </div>
                        </DialogFooter>
                    </React.Fragment>
                }
                {delayed &&
                    <React.Fragment>
                        <div className={layout.paddingMedium}>
                            {t("DelayedWhy")}
                        </div>
                        <DialogFooter>
                            <div className={layout.paddingMedium}>
                                <PushButton success onClick={() => onEndRentalClick(DelayReason.DelayedReturn)}>
                                    {t("DelayedReturn")}
                                </PushButton>
                            </div>
                            <div className={layout.paddingMedium}>
                                <PushButton danger onClick={() => onEndRentalClick(DelayReason.Forgot)}>
                                    {t("Forgot")}
                                </PushButton>
                            </div>
                        </DialogFooter>
                    </React.Fragment>
                }
            </Dialog>

            <CustomFieldForm
                open={customFieldDialog}
                onClose={handleCustomFieldDialogClose}
                inputs={customFieldInputs}
                onChange={handleCustomFieldChange}
                onSubmit={onCustomFieldSubmit}
            />

            <ItemConditionForm
                open={itemConditionDialog}
                onClose={handleItemConditionDialogClose}
                onSubmit={onItemConditionSubmit}
            />

            {previousRental &&
                <ViewFooter className={classNames(theme.lessContrast, layout.paddingMedium, layout.spaceBetween)}>
                    <ViewFooterItem className={typography.bigger}>
                        <div style={{paddingTop: '0.5em'}}>
                            {t("NextRentalDelayInfo")}
                        </div>
                    </ViewFooterItem>
                    <ViewFooterItem>
                        <PushButton primary onClick={() => setContactView(true)}>{t("Contact")}</PushButton>
                    </ViewFooterItem>
                </ViewFooter>
            }
        </View>
    );
}

interface CustomFieldFormProps {
    open: boolean;
    onClose: () => void;
    inputs: CustomFieldInput[];
    onChange: (slug: string, value: string) => void;
    onSubmit: () => void;
}

function CustomFieldForm({open, onClose, inputs, onChange, onSubmit}: CustomFieldFormProps) {
    const {t} = useLocalization();
    const [inputType, setInputType] = useTypeRef();

    const [step, setStep] = useState(1);

    const maxStep = useMemo(() => inputs.length, [inputs]);

    function handleBack() {
        if (step === 1) {
            onClose();
        } else {
            setStep(prev => prev > 1 ? prev - 1 : prev);
        }
    }

    function handleNext() {
        if (step === maxStep) {
            onSubmit();
        } else {
            setStep(prev => prev < maxStep ? prev + 1 : prev);
        }
    }

    const keyboardLayout: KeyboardLayout = useMemo(() => {
        try {
            const input = inputs[step - 1];
            switch (input.type) {
                case CustomFieldType.STRING:
                    return TextKeyboardLayout;
                case CustomFieldType.MILEAGE:
                    return PhoneKeyboardLayout;
            }
        } catch (err) {
            console.error('Failed to get current input', err);
        }
        return TextKeyboardLayout;
    }, [inputs, step]);

    const canNext = useMemo(() => {
        try {
            const input = inputs[step - 1];
            return (input.required && !!input.value) || !input.required;
        } catch (err) {
            console.error('Failed to determinate input requirement', err);
        }
        return false;
    }, [step, inputs]);

    return (

        <Dialog
            open={open}
            onBackdropClick={onClose}
            class={classNames(styles.fullScreenDialog)}
        >
            <DialogHeader title={t('InformationNeeded')}/>
            <div className={classNames(layout.paddingSmall, layout.vertical, layout.center, layout.alignCenter, styles.formContent)}>
                <p style={{textAlign: 'center'}}>{t('AdditionalInformationNeeded')}</p>
                {inputs.map(({name, value, slug}, i) => {
                    if (i === step - 1) {
                        return (
                            <TextInput
                                outline={true}
                                placeholder={name}
                                className={classNames(styles.textInput)}
                                value={value}
                                typeRef={setInputType}
                                onChange={value => onChange(slug, value)}

                            />
                        )
                    }
                    return <></>;
                })}
            </div>

            <div className={styles.keyboardContainer}>
                <Keyboard onType={inputType} layout={keyboardLayout}/>
            </div>

            <div className={classNames(layout.paddingSmall, layout.horizontal, layout.spaceBetween)}>
                <PushButton primary onClick={handleBack}>Back</PushButton>
                <PushButton primary onClick={handleNext} disabled={!canNext}>
                    {step === maxStep && t('Submit')}
                    {step !== maxStep && t('Next')}
                </PushButton>
            </div>
        </Dialog>
    )
}

interface ItemConditionFormProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (type?: TicketType, needInspection?: boolean, message?: string) => Promise<void>;
}

function ItemConditionForm({open, onClose, onSubmit}: ItemConditionFormProps) {
    const {t} = useLocalization();
    const tenant = useTenant();
    const [inputType, setInputType] = useTypeRef();

    const [type, setType] = useState<TicketType>();
    const [message, setMessage] = useState('');
    const [messageMode, setMessageMode] = useState(false);

    async function handleConditionSelect(t?: TicketType, needInspection?: boolean) {
        if (t === undefined) {
            await onSubmit(undefined, needInspection, undefined);
            return;
        }
        setType(t);
        setMessageMode(true);
    }

    async function handleConditionSubmit() {
        await onSubmit(type, true, message);
    }

    return (
        <Dialog
            open={open}
            onBackdropClick={onClose}
            class={classNames(styles.fullScreenDialog)}
        >
            <DialogHeader title={t("ItemCondition")} />
            <div className={layout.paddingSmall} style={{ textAlign: 'center' }}>
                {t("ItemConditionInfo")}
            </div>
            {!messageMode && (
                <DialogFooter>
                    <div className={layout.paddingMedium}>
                        <PushButton success onClick={handleConditionSelect}>{t("ItemConditionGood")}</PushButton>
                    </div>
                    {!tenant?.enabledTicketOnCube && (
                        <div className={layout.paddingMedium}>
                            <PushButton danger onClick={() => handleConditionSelect(undefined, true)}>{t("ItemConditionNeedInspection")}</PushButton>
                        </div>
                    )}
                    {tenant?.enabledTicketOnCube && (
                        <Fragment>
                            <div className={layout.paddingSmall}>
                                <PushButton danger onClick={() => handleConditionSelect(TicketType.Defective)}>{t("ItemConditionDefective")}</PushButton>
                            </div>
                            <div className={layout.paddingSmall}>
                                <PushButton danger onClick={() => handleConditionSelect(TicketType.Lost)}>{t("ItemConditionLost")}</PushButton>
                            </div>
                            <div className={layout.paddingSmall}>
                                <PushButton danger onClick={() => handleConditionSelect(TicketType.Maintenance)}>{t("ItemConditionMaintenance")}</PushButton>
                            </div>
                            <div className={layout.paddingSmall}>
                                <PushButton danger onClick={() => handleConditionSelect(TicketType.Misc)}>{t("ItemConditionMisc")}</PushButton>
                            </div>
                        </Fragment>
                    )}

                    <div className={layout.paddingMedium}>
                        <PushButton onClick={onClose}>{t('Cancel')}</PushButton>
                    </div>
                </DialogFooter>
            )}
            {messageMode && (
                <>
                    <div className={classNames(layout.paddingSmall, layout.horizontal, layout.center, styles.formContent)}>
                        <TextInput
                            outline={true}
                            placeholder="Describe the problem"
                            className={classNames(styles.textInput)}
                            value={message}
                            typeRef={setInputType}
                            onChange={setMessage}
                        />
                    </div>

                    <div className={styles.keyboardContainer}>
                        <Keyboard onType={inputType} layout={TextKeyboardLayout}/>
                    </div>

                    <div className={classNames(layout.paddingSmall, layout.horizontal, layout.spaceBetween)}>
                        <PushButton primary onClick={() => setMessageMode(false)}>{t('Back')}</PushButton>
                        <PushButton primary onClick={handleConditionSubmit}>{t('Submit')}</PushButton>
                    </div>
                </>
            )}
        </Dialog>
    )
}

const styles = stylesheet({
    fullScreenDialog: {
        width: percent(100),
        height: percent(100),
        display: 'flex',
        flexDirection: 'column',
    },
    formContent: {
        flexGrow: 1,
        textAlign: 'center',
    },
    keyboardContainer: {
        padding: "0.25rem",
    },
    textInput: {
        fontWeight: "bold",
        width: "90%",
        height: "4rem",
        fontSize: "2em",
        margin: "0.3em"
    },
})
